import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, SendHorizontal, UserRound } from 'lucide-react';
import PageEmptyState from '../PageEmptyState/PageEmptyState';
import { useIsCompact } from '../Layout/shellState';
import { sanitizeMessageContent, sanitizeName, validateAndSanitizeInput } from '../../utils/sanitize';
import { useI18n } from '../../i18n/I18nContext';
import { formatLongDate, formatTime } from '../../utils/datetime';
import { initialsOf, avatarTone } from '../Avatar/Avatar';
import { hasTouchKeyboard } from './when';
import './MessageDetailView.css';

// Scroll the message list (not the page) to its newest message.
function scrollToEnd(list, smooth) {
  if (!list) return;
  if (typeof list.scrollTo === 'function') list.scrollTo({ top: list.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  else list.scrollTop = list.scrollHeight;
}

const dayOf = (value) => (value ? new Date(value).toDateString() : '');

// The thread's own loading shape: bubbles of both sides, so "no messages yet" is never drawn for a
// conversation whose messages are still on their way.
function ThreadSkeleton({ label }) {
  return (
    <div className="mdv-skel" role="status" aria-busy="true" aria-label={label}>
      <div className="mdv-skel-msg" aria-hidden="true">
        <span className="ui-skel mdv-skel-avatar" />
        <span className="ui-skel mdv-skel-bubble is-wide" />
      </div>
      <div className="mdv-skel-msg is-out" aria-hidden="true">
        <span className="ui-skel mdv-skel-bubble is-mid" />
      </div>
      <div className="mdv-skel-msg" aria-hidden="true">
        <span className="ui-skel mdv-skel-avatar" />
        <span className="ui-skel mdv-skel-bubble is-short" />
      </div>
      <div className="mdv-skel-msg is-out" aria-hidden="true">
        <span className="ui-skel mdv-skel-bubble is-wide" />
      </div>
      <span className="visually-hidden">{label}</span>
    </div>
  );
}

/**
 * One conversation: the header (who, online or not, and a way to their profile), the messages and
 * the composer. On a keyboard Enter sends and Shift+Enter breaks the line; on a touch screen Enter
 * is a line break and only the send button sends.
 *
 * `onSendMessage(text)` may return a promise; when it resolves to `false` the send did not land
 * and the text goes back into the box instead of being lost. `loading` is true while the first
 * page of messages is on its way.
 */
function MessageDetailView({
  conversation, messages, onBack, onSendMessage, sending, onTyping, isTyping,
  hasMore, loadingMore, onLoadMore, loading = false, loadFailed = false, onRetry,
}) {
  const { t } = useI18n();
  const compact = useIsCompact();
  const [messageText, setMessageText] = useState('');
  const [inputError, setInputError] = useState('');
  const messagesRef = useRef(null);
  const textareaRef = useRef(null);
  const backRef = useRef(null);
  const newestIdRef = useRef(null);
  const loadAnchorRef = useRef(null);

  // On a phone the thread has replaced the list, and the list took the keyboard focus with it: the
  // thread starts at the way back (a button, so no on-screen keyboard opens).
  useEffect(() => {
    if (compact && backRef.current) backRef.current.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the thread opens
  }, []);

  // Follow the conversation to its newest message. Loading older messages above, or
  // editing one in the middle, leaves the newest where it was and must not scroll.
  useEffect(() => {
    const newestId = messages.length ? messages[messages.length - 1].id : null;
    const newestChanged = newestIdRef.current !== newestId;
    const firstLoad = newestIdRef.current === null;
    newestIdRef.current = newestId;
    if (newestChanged || isTyping) scrollToEnd(messagesRef.current, !firstLoad);
  }, [messages, isTyping]);

  // Older messages grow the list upward; keep the message the agent was reading in place.
  useLayoutEffect(() => {
    const anchor = loadAnchorRef.current;
    const list = messagesRef.current;
    if (!anchor || !list || loadingMore) return;
    loadAnchorRef.current = null;
    list.scrollTop = anchor.top + (list.scrollHeight - anchor.height);
  }, [messages, loadingMore]);

  const handleLoadMore = () => {
    const list = messagesRef.current;
    if (list) loadAnchorRef.current = { top: list.scrollTop, height: list.scrollHeight };
    onLoadMore();
  };

  const resetHeight = () => {
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (sending || !messageText.trim()) return;

    const validation = validateAndSanitizeInput(messageText, { minLength: 1, maxLength: 2000 });
    if (!validation.valid) {
      setInputError(t('messaging.sendMessageFailed'));
      return;
    }

    const draft = messageText;
    setInputError('');
    setMessageText('');
    resetHeight();
    const sent = await onSendMessage(validation.sanitized);
    // It did not go out: the text comes back (unless the agent has already typed something new).
    if (sent === false) setMessageText((current) => current || draft);
    if (textareaRef.current) textareaRef.current.focus({ preventScroll: true });
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    // Enter that confirms an IME composition, or a touch keyboard's return key, is not "send".
    if (e.nativeEvent?.isComposing || hasTouchKeyboard()) return;
    e.preventDefault();
    handleSend(e);
  };

  const handleTextareaChange = (e) => {
    setMessageText(e.target.value);
    setInputError('');
    if (onTyping) onTyping();
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  const formatDateSeparator = (value) => {
    if (!value) return '';
    const date = new Date(value);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return t('messaging.today');
    if (date.toDateString() === yesterday.toDateString()) return t('messaging.yesterday');
    return formatLongDate(date, { year: 'auto' });
  };

  // Which messages open a run from one person (name avatar), which close it (time), which start a day.
  const enriched = messages.map((msg, i) => {
    const prev = messages[i - 1];
    const next = messages[i + 1];
    const day = dayOf(msg.createdAt);
    const showDateSep = Boolean(day) && day !== dayOf(prev?.createdAt);
    const isGrouped = Boolean(prev) && !showDateSep && prev.isFromMe === msg.isFromMe;
    const isGroupedNext = Boolean(next) && dayOf(next.createdAt) === day && next.isFromMe === msg.isFromMe;
    return { ...msg, showDateSep, isGrouped, isGroupedNext };
  });

  const participantName = sanitizeName(conversation.participantName || t('messaging.unknownUser'));
  const tone = avatarTone(conversation.id || participantName);
  const profileTo = conversation.peerId ? `/students/${encodeURIComponent(conversation.peerId)}` : null;

  return (
    <div className="mdv-root">
      {/* Header: who, and whether they are online */}
      <header className="mdv-top">
        <button
          ref={backRef}
          type="button"
          onClick={onBack}
          className="ds-btn ds-btn-secondary ds-btn-icon mdv-back"
          aria-label={t('messaging.back')}
        >
          <ChevronLeft aria-hidden="true" />
        </button>

        <span className="ui-avatar" data-tone={tone} aria-hidden="true">
          {initialsOf(participantName)}
          {conversation.isOnline && <span className="ui-presence" />}
        </span>
        <div className="mdv-header-meta">
          <h2 className="mdv-header-name">{participantName}</h2>
          <span className={`mdv-header-status${conversation.isOnline ? ' mdv-header-status--online' : ''}`}>
            {conversation.isOnline ? t('messaging.activeNow') : t('messaging.offline')}
          </span>
        </div>
        {profileTo && (
          <Link
            to={profileTo}
            className="ds-btn ds-btn-ghost ds-btn-sm mdv-profile"
            aria-label={`${t('messaging.openProfile')}: ${participantName}`}
          >
            <UserRound aria-hidden="true" />
            <span className="mdv-profile-label">{t('messaging.openProfile')}</span>
          </Link>
        )}
      </header>

      {/* Messages */}
      <div className="mdv-messages" ref={messagesRef} role="log" aria-live="polite" aria-label={participantName} tabIndex={0}>
        {enriched.length === 0 && loading ? (
          <ThreadSkeleton label={t('messaging.loading')} />
        ) : enriched.length === 0 && loadFailed ? (
          <PageEmptyState
            variant="error"
            size="lg"
            title={t('messaging.loadMessagesFailed')}
            onRetry={onRetry}
          />
        ) : enriched.length === 0 ? (
          <PageEmptyState
            size="lg"
            kind="messages"
            title={t('messaging.noMessagesYet')}
            description={t('messaging.noMessagesHint')}
          />
        ) : (
          <>
            {hasMore && (
              <button
                type="button"
                className="ds-btn ds-btn-secondary ds-btn-sm mdv-load-more"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? t('messaging.loading') : t('messaging.loadEarlier')}
              </button>
            )}
            {enriched.map((msg, i) => (
              <React.Fragment key={msg.id || i}>
                {msg.showDateSep && (
                  <div className="mdv-date-sep">
                    <span>{formatDateSeparator(msg.createdAt)}</span>
                  </div>
                )}
                <div
                  className={[
                    'mdv-msg',
                    msg.isFromMe ? 'mdv-msg--out' : 'mdv-msg--in',
                    msg.isGrouped ? 'mdv-msg--grouped' : '',
                    msg.isGroupedNext ? 'mdv-msg--joined' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {!msg.isFromMe && (
                    <span
                      className={`ui-avatar ui-avatar--sm mdv-avatar${msg.isGrouped ? ' is-ghost' : ''}`}
                      data-tone={tone}
                      aria-hidden="true"
                    >
                      {!msg.isGrouped && initialsOf(sanitizeName(msg.senderName))}
                    </span>
                  )}
                  <div className="mdv-msg-body">
                    <div className="mdv-bubble">
                      <p>{sanitizeMessageContent(msg.content)}</p>
                    </div>
                    {!msg.isGroupedNext && <span className="mdv-time">{formatTime(msg.createdAt)}</span>}
                  </div>
                </div>
              </React.Fragment>
            ))}

            {isTyping && (
              <div className="mdv-msg mdv-msg--in mdv-typing" role="status" aria-label={t('messaging.typing')}>
                <span className="ui-avatar ui-avatar--sm mdv-avatar" data-tone={tone} aria-hidden="true">
                  {initialsOf(participantName)}
                </span>
                <div className="mdv-msg-body">
                  <div className="mdv-bubble mdv-bubble--typing">
                    <span className="mdv-dots" aria-hidden="true"><span /><span /><span /></span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Composer */}
      <div className="mdv-composer">
        {inputError && <div className="ui-error mdv-input-error" role="alert">{inputError}</div>}
        <form onSubmit={handleSend} className="mdv-form">
          <div className="mdv-input-shell">
            <textarea
              ref={textareaRef}
              value={messageText}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder={t('messaging.composerPlaceholder')}
              aria-label={t('messaging.composerPlaceholder')}
              maxLength={2000}
              rows={1}
              className="mdv-textarea"
            />
            <button
              type="submit"
              disabled={!messageText.trim() || sending}
              className={`ds-btn ds-btn-primary ds-btn-icon mdv-send${sending ? ' is-loading' : ''}`}
              aria-label={t('messaging.sendMessage')}
            >
              <SendHorizontal aria-hidden="true" />
            </button>
          </div>
          <p className="mdv-hint">{t('messaging.composerHint')}</p>
        </form>
      </div>
    </div>
  );
}

export default MessageDetailView;
